// Maintenance detection for belong-web.
//
// We can't read cross-origin 503s from the API reliably (the maintenance/gateway
// 503 usually carries no CORS headers, so the browser hands axios an opaque
// network error). Instead we consult belong-next's own /api/maintenance-windows
// route — it's our function, so we control its CORS headers and can read it
// cross-origin. Contract: 200 [] = healthy, 200 [{…}] or 503 = maintenance.

const HOST = process.env.NEXT_PUBLIC_HOST;
const STATUS_URL = `${HOST}/api/maintenance-windows`;

export function redirectToMaintenancePage() {
  window.location.replace(`${HOST}/maintenance`);

  // Never resolves, so axios interceptor chains halt instead of continuing
  // to run against a page that is about to be replaced.
  return new Promise(() => {});
}

/**
 * Asks belong-next's maintenance-windows route whether maintenance is active.
 * Plain GET with no custom headers, so it stays a CORS "simple request" (no
 * preflight). Fails open: if the oracle itself is unreachable we return false
 * rather than locking users out of a healthy app.
 */
export async function isMaintenanceActive() {
  try {
    const res = await fetch(STATUS_URL, { method: 'GET' });

    // Our route returns 503 when Octane is unreachable or reports a problem.
    if (res.status === 503) {
      return true;
    }

    // Any other non-2xx is unexpected; don't redirect on it.
    if (!res.ok) {
      return false;
    }

    const windows = await res.json();

    return Array.isArray(windows) && windows.length > 0;
  } catch {
    return false;
  }
}

async function checkAndRedirectIfMaintenance() {
  if (await isMaintenanceActive()) {
    redirectToMaintenancePage();
  }
}

/**
 * Polls for maintenance so users already inside the SPA get redirected even if
 * they never fire another API request. Runs an immediate check (covers cold
 * loads) then repeats on an interval. Returns the interval id.
 */
export function startMaintenanceHeartbeat(intervalMs = 30000) {
  checkAndRedirectIfMaintenance();

  return window.setInterval(checkAndRedirectIfMaintenance, intervalMs);
}
